/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { Range, RangeWithKey } from "react-date-range";
import styled, { css } from "styled-components";
import {
  Flex,
  DayPicker,
  Text,
  Spinner,
  Datepicker,
  theme,
  Button,
} from "@nordcloud/gnui";
import { FlexContainer } from "~/components";
import {
  ContractEditModeType,
  ContractLoadingType,
} from "~/components/Contract/ContractItem";
import { InlineActionButton } from "~/components/Styled/InlineActionButton";
import { dateFormat } from "~/constants";
import { useClickOutside } from "~/tools";

type ContractInlineDatePickerProps = {
  property: "label" | "link" | "ends" | "signed" | "starts";
  date: Dayjs | null;
  setDate: React.Dispatch<React.SetStateAction<Dayjs | null>>;
  isLoading: ContractLoadingType;
  setIsLoading: React.Dispatch<React.SetStateAction<ContractLoadingType>>;
  isEditMode: ContractEditModeType;
  setIsEditMode: React.Dispatch<React.SetStateAction<ContractEditModeType>>;
  left?: boolean;
  canEdit?: boolean;
};

type InnerType = {
  left: boolean;
};

const Wrap = styled.div`
  position: relative;
`;

const Inner = styled.div<InnerType>`
  background-color: ${theme.colors.white};
  position: absolute;
  filter: drop-shadow(0 1.5rem 1.5rem rgba(0, 0, 0, 0.2));
  z-index: ${theme.zindex.popup};
  bottom: 50%;
  ${({ left }) =>
    left &&
    css`
      right: 0;
    `}
`;

function ContractInlineDatePicker({
  date,
  setDate,
  property,
  isLoading,
  isEditMode,
  setIsEditMode,
  left,
  canEdit = true,
}: ContractInlineDatePickerProps) {
  dayjs.extend(utc);

  const clearDate = () => {
    setDate(null);
    setIsEditMode((prevState) => ({
      ...prevState,
      [property]: false,
    }));
  };

  const wrapperRef = React.useRef(null);

  useClickOutside(
    wrapperRef,
    () => {
      setIsEditMode((prevState) => ({ ...prevState, [property]: false }));
    },
    isEditMode[property]
  );

  function EditForm() {
    return (
      <FlexContainer direction="column" align="flex-start" ref={wrapperRef}>
        <Datepicker>
          <DayPicker
            mode="single"
            selected={date ? dayjs(date).toDate() : dayjs(new Date()).toDate()}
            // @ts-ignore
            onDayClick={(newD: Range | { selection: RangeWithKey } | Date) => {
              if ("getFullYear" in newD) {
                const newDate = dayjs
                  .utc()
                  .year(newD.getFullYear())
                  .month(newD.getMonth())
                  .date(newD.getDate())
                  .hour(0)
                  .minute(0)
                  .second(0)
                  .millisecond(0);
                setDate(newDate);
              }
              setIsEditMode((prevState) => ({
                ...prevState,
                [property]: false,
              }));
            }}
          />
        </Datepicker>
        <Button
          type="button"
          mt="0.5rem"
          icon="trash"
          size="md"
          severity="low"
          onClick={clearDate}
        >
          Clear
        </Button>
      </FlexContainer>
    );
  }

  if (isLoading[property]) {
    return (
      <Flex>
        <Spinner size="sm" />
        <Text weight="regular" tag="div" css={{ marginLeft: "1rem" }}>
          Loading ...
        </Text>
      </Flex>
    );
  }

  function DisplayText() {
    return (
      <>
        <Text tag="span" css={{ minHeight: "24px", minWidth: "9rem" }}>
          {date ? dayjs(date).format(`${dateFormat}`) : "-"}
        </Text>
        {canEdit && (
          <InlineActionButton
            type="button"
            ml="0.5rem"
            icon="edit"
            size="sm"
            severity="low"
            onClick={() =>
              setIsEditMode((prevState) => ({ ...prevState, [property]: true }))
            }
          />
        )}
        <Wrap>
          <Inner left={!!left}>{isEditMode[property] && <EditForm />}</Inner>
        </Wrap>
      </>
    );
  }

  return <FlexContainer align="center">{DisplayText()}</FlexContainer>;
}

export { ContractInlineDatePicker };
